/*
 * @fileOverView Search Results Analytics component definition
 *
 * This component is different from most others in the site in that it has no
 * children and doesn't render anything. Instead, it pays attention to state and
 * markup changes across the document and reacts accordingly. This approach was
 * taken because it's only needed for analytics, and wrapping the entire search
 * results page and doing things the "Vue way" would have been quite complex
 * with an much higher chance of introducing regressions.
 */

import Vue from 'vue';
import Analytics from 'mixins/analytics.mixin';

/**
 * Search Results Analytics Vue
 * @type {class}
 */
Vue.component('search-results-analytics-vue', {
  mixins: [
    Analytics
  ],

  props: [
    'id'
  ],

  mounted() {
    this.$store.subscribe((mutation, state) => {
      // Reports viewed event after initial page load and any filter change.
      if (typeof mutation.payload.id !== 'undefined' && mutation.payload.id === 'mjff-search' && mutation.type === 'filters/setValue') {
        this.reportListingViewed();
      }
    });

    this.attachObserver();
    this.attachResultLinkClickHandlers();
  },

  methods: {
    /**
     * Attaches an observer to reattach click handlers after filtering/paging.
     *
     * Filtering could be listened for via the store, but paging can't. There is
     * no other way to know when additional pages have been added to the DOM and
     * this is the easiest way to reattach handlers after either one happens.
     */
    attachObserver() {
      const container = document.getElementById(this.id);

      if (container) {
        const observer = new MutationObserver(this.attachResultLinkClickHandlers);

        observer.observe(container, { childList: true, subtree: true });
      }
    },

    /**
     * Attaches handlers to listing links so they can report when clicked.
     */
    attachResultLinkClickHandlers() {
      const resultLinks = document.querySelectorAll('.js-search-results-link');

      for (let i = 0; i < resultLinks.length; i++) {
        resultLinks[i].addEventListener('click', this.handleResultLinkClick);
      }
    },

    /**
     * Handles result link clicks.
     */
    handleResultLinkClick(e) {
      this.reportLinkClicked(e.target);
    },

    /**
     * Reports the result link clicked event.
     *
     * @param {Object} linkEl - The clicked linked element.
     */
    reportLinkClicked(linkEl) {
      // The title is cleaned to match the result's Page Loaded title.
      const listItem = linkEl.closest('li');
      const title = listItem.querySelector('.js-search-results-title').textContent.toLowerCase().replace(/[^\w\s]/gi, '');
      const position = $(listItem).index() + 1;

      this.pushSearchResultsClickedEvent(title, position);
    },

    /**
     * Reports the listing viewed event.
     */
    reportListingViewed: function () {
      const store = this.$store.state.filters.sets['mjff-search'];
      const term = decodeURIComponent(store.values.query);
      const order = store.values.sort_by ? store.values.sort_by : 'Relevance';
      const countEl = document.querySelector('.js-search-results-count');
      let count = 0;

      if (countEl) {
        count = countEl.innerHTML.replace(/[^0-9]+/g, '');
      }

      this.pushSearchResultsListingViewedEvent(term, order, count);
    }
  },

  render (h) {
    // Only renders an empty div. See comments atop the file.
    return h('div');
  }
});
